import React, { useCallback, useEffect, useMemo, useState } from "react";
import { ProgressSpinner } from "primereact/progressspinner";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { InputText } from "primereact/inputtext";
import { Button } from "primereact/button";
import { useNavigate } from "react-router-dom";
import {
  excludeCustomerFromMissingVatId,
  getCustomersMissingVatId,
  patchCustomerVatId,
  removeCustomerVatIdExclusion,
} from "../../../../services/clearing/customers";
import { validateEuVatId } from "../../../../utils/euVatId";
import CollapsibleSection from "../../../../components/CollapsibleSection";
import SectionStatusBadge from "../SectionStatusBadge";
import RemoveFromListDialog from "./RemoveFromListDialog";
import {
  formatExclusionTimestamp,
  reasonLabel,
} from "../../../../utils/vatIdExclusion";

const customerName = (customer) =>
  customer?.company_name || customer?.account_holder_name || customer?.id;

// Both the VAT input and the row actions sit inside a row that navigates on
// click, so anything interactive has to keep its clicks to itself.
const StopRowClick = ({ children, style }) => (
  <div onClick={(e) => e.stopPropagation()} style={style}>
    {children}
  </div>
);

const VatIdEditor = ({ customer, onSave, saving }) => {
  const [draft, setDraft] = useState("");
  const [error, setError] = useState(null);

  const onChange = (e) => {
    const next = e.target.value;
    setDraft(next);
    if (!next) {
      setError(null);
      return;
    }
    const result = validateEuVatId(next);
    setError(result.ok ? null : result.error);
  };

  const disabled = saving || !draft || !!error;

  return (
    <StopRowClick style={{ display: "flex", flexDirection: "column", gap: "2px" }}>
      <div style={{ display: "flex", gap: "6px" }}>
        <InputText
          value={draft}
          onChange={onChange}
          placeholder="DE123456789"
          disabled={saving}
          className={error ? "p-invalid" : ""}
          style={{ fontFamily: "monospace" }}
        />
        <Button
          label={saving ? "Saving…" : "Save"}
          disabled={disabled}
          onClick={() => onSave(customer, draft)}
        />
      </div>
      {error && <small className="text-red-500">{error}</small>}
    </StopRowClick>
  );
};

export default function MissingVatIdSection({ toastRef }) {
  const navigate = useNavigate();
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [savingIds, setSavingIds] = useState(() => new Set());
  const [showRemoved, setShowRemoved] = useState(false);
  const [pendingRemoval, setPendingRemoval] = useState(null);
  const [removing, setRemoving] = useState(false);
  const [removeError, setRemoveError] = useState(null);
  const [undoingIds, setUndoingIds] = useState(() => new Set());

  // One request covers both views: asking for the excluded rows too keeps the
  // "Removed" count honest without a second round trip, and an undo moves a row
  // between the two lists without refetching anything.
  const loadCustomers = useCallback(async () => {
    const data = await getCustomersMissingVatId({ includeExcluded: true });
    return Array.isArray(data) ? data : [];
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(false);
    loadCustomers()
      .then((data) => {
        if (cancelled) return;
        setCustomers(data);
      })
      .catch(() => {
        if (cancelled) return;
        setError(true);
      })
      .finally(() => {
        if (cancelled) return;
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [loadCustomers]);

  const worklist = useMemo(
    () => customers.filter((c) => !c.vat_id_exclusion),
    [customers]
  );
  const removed = useMemo(
    () => customers.filter((c) => c.vat_id_exclusion),
    [customers]
  );

  // Undoing the last removal would otherwise leave an empty table on screen with
  // no obvious way back.
  useEffect(() => {
    if (showRemoved && removed.length === 0) {
      setShowRemoved(false);
    }
  }, [showRemoved, removed.length]);

  const handleCopyEmails = async () => {
    const emails = worklist.map((c) => c.email).join(", ");
    try {
      await navigator.clipboard.writeText(emails);
      toastRef?.current?.show({
        severity: "success",
        summary: "Copied",
        detail: `Copied ${worklist.length} email addresses to clipboard.`,
        life: 3000,
      });
    } catch {
      toastRef?.current?.show({
        severity: "error",
        summary: "Error",
        detail: "Failed to copy emails to clipboard.",
        life: 3000,
      });
    }
  };

  const handleSave = async (customer, vatId) => {
    const normalizedVatId = vatId.trim().toUpperCase();
    setSavingIds((prev) => {
      const next = new Set(prev);
      next.add(customer.id);
      return next;
    });
    try {
      await patchCustomerVatId(customer.id, normalizedVatId);
      setCustomers((prev) => prev.filter((c) => c.id !== customer.id));
      toastRef?.current?.show({
        severity: "success",
        summary: "Saved",
        detail: `VAT ID saved for ${customerName(customer)}.`,
        life: 3000,
      });
    } catch (e) {
      toastRef?.current?.show({
        severity: "error",
        summary: "Error",
        detail: "Failed to save VAT ID — please try again.",
        life: 4000,
      });
    } finally {
      setSavingIds((prev) => {
        const next = new Set(prev);
        next.delete(customer.id);
        return next;
      });
    }
  };

  const handleConfirmRemoval = async (reason, note) => {
    const customer = pendingRemoval;
    setRemoving(true);
    setRemoveError(null);
    try {
      const updated = await excludeCustomerFromMissingVatId(customer.id, {
        reason,
        note,
      });
      // The response carries who removed it and when, which we cannot invent
      // locally. It is a different shape from the worklist row, though, so keep
      // the row we already have and take only the exclusion from it.
      const exclusion = updated?.vat_id_exclusion;
      if (exclusion) {
        setCustomers((prev) =>
          prev.map((c) =>
            c.id === customer.id ? { ...c, vat_id_exclusion: exclusion } : c
          )
        );
      } else {
        setCustomers(await loadCustomers());
      }
      setPendingRemoval(null);
      toastRef?.current?.show({
        severity: "success",
        summary: "Removed from the list",
        detail: `${customerName(customer)} — ${reasonLabel(reason)}.`,
        life: 4000,
      });
    } catch (e) {
      // Kept in the dialog rather than a toast: the form is still open and the
      // message says what to change.
      setRemoveError(e?.message || "Failed to remove the customer from the list.");
    } finally {
      setRemoving(false);
    }
  };

  const handleUndo = async (customer) => {
    setUndoingIds((prev) => {
      const next = new Set(prev);
      next.add(customer.id);
      return next;
    });
    try {
      await removeCustomerVatIdExclusion(customer.id);
      setCustomers((prev) =>
        prev.map((c) =>
          c.id === customer.id ? { ...c, vat_id_exclusion: null } : c
        )
      );
      toastRef?.current?.show({
        severity: "success",
        summary: "Back on the list",
        detail: `${customerName(customer)} is on the missing VAT ID list again.`,
        life: 4000,
      });
    } catch (e) {
      toastRef?.current?.show({
        severity: "error",
        summary: "Error",
        detail: e?.message || "Failed to put the customer back on the list.",
        life: 4000,
      });
    } finally {
      setUndoingIds((prev) => {
        const next = new Set(prev);
        next.delete(customer.id);
        return next;
      });
    }
  };

  const loaded = !loading && !error;
  const headerActions = loaded ? (
    <>
      {!showRemoved && worklist.length > 0 && (
        <Button
          label="Copy all emails"
          icon="pi pi-copy"
          outlined
          onClick={handleCopyEmails}
        />
      )}
      {showRemoved ? (
        <Button
          label="Back to the list"
          icon="pi pi-arrow-left"
          text
          onClick={() => setShowRemoved(false)}
        />
      ) : (
        removed.length > 0 && (
          <Button
            label={`Removed (${removed.length})`}
            icon="pi pi-eye-slash"
            text
            onClick={() => setShowRemoved(true)}
          />
        )
      )}
    </>
  ) : null;

  return (
    <CollapsibleSection
      title="Business customers missing VAT ID (Germany)"
      storageKey="clearing.missingVatId"
      badge={
        <SectionStatusBadge
          loading={loading}
          error={error}
          count={worklist.length}
          singular="customer"
          plural="customers"
        />
      }
      actions={headerActions}
    >
      {loading && (
        <div className="flex align-items-center gap-2">
          <ProgressSpinner style={{ width: "1.5rem", height: "1.5rem" }} strokeWidth="4" />
          <span className="text-color-secondary">Loading customers missing a VAT ID…</span>
        </div>
      )}

      {!loading && error && (
        <div className="flex align-items-center gap-2 text-red-500">
          <i className="pi pi-times-circle" />
          <span>Failed to load customers. Try reloading the page.</span>
        </div>
      )}

      {loaded && showRemoved && (
        <>
          <span className="text-color-secondary text-sm block mb-3">
            These customers were taken off the list on purpose. Undo puts one
            back.
          </span>
          <DataTable value={removed} dataKey="id" rowHover emptyMessage="Nothing has been removed.">
            <Column field="company_name" header="Company" sortable />
            <Column field="account_holder_name" header="Account holder" sortable />
            <Column
              header="Reason"
              sortable
              sortField="vat_id_exclusion.reason"
              body={(row) => reasonLabel(row.vat_id_exclusion?.reason)}
            />
            <Column
              header="Note"
              body={(row) => row.vat_id_exclusion?.note || "—"}
            />
            <Column
              header="Removed by"
              sortable
              sortField="vat_id_exclusion.excluded_by"
              body={(row) => row.vat_id_exclusion?.excluded_by || "—"}
            />
            <Column
              header="Removed on"
              sortable
              sortField="vat_id_exclusion.excluded_at"
              body={(row) => formatExclusionTimestamp(row.vat_id_exclusion?.excluded_at)}
            />
            <Column
              body={(row) => (
                <Button
                  label="Undo"
                  icon="pi pi-undo"
                  outlined
                  loading={undoingIds.has(row.id)}
                  onClick={() => handleUndo(row)}
                />
              )}
              style={{ width: "8rem" }}
            />
          </DataTable>
        </>
      )}

      {loaded && !showRemoved && worklist.length === 0 && (
        <div className="flex align-items-center gap-2 text-green-600">
          <i className="pi pi-check-circle text-xl" />
          <span>All BUSINESS customers in Germany have a VAT ID on file.</span>
        </div>
      )}

      {loaded && !showRemoved && worklist.length > 0 && (
        <>
          <span className="text-color-secondary text-sm block mb-3">
            Click a row to open customer details. Enter a VAT ID to save inline,
            or remove a customer that will never have one.
          </span>
          <DataTable
            value={worklist}
            dataKey="id"
            selectionMode="single"
            onRowSelect={(e) => navigate(`/customers/${e.data.id}`)}
            rowHover
          >
            <Column field="company_name" header="Company" sortable />
            <Column field="account_holder_name" header="Account holder" sortable />
            <Column field="city" header="City" sortable />
            <Column field="email" header="Email" />
            <Column
              header="VAT ID"
              body={(rowData) => (
                <VatIdEditor
                  customer={rowData}
                  saving={savingIds.has(rowData.id)}
                  onSave={(c, vatId) => handleSave(c, vatId)}
                />
              )}
            />
            <Column
              style={{ width: "7rem" }}
              body={(rowData) => (
                <StopRowClick>
                  <Button
                    label="Remove"
                    icon="pi pi-times"
                    severity="danger"
                    outlined
                    onClick={() => {
                      setRemoveError(null);
                      setPendingRemoval(rowData);
                    }}
                    tooltip="Take this customer off the list with a reason"
                    tooltipOptions={{ position: "left" }}
                  />
                </StopRowClick>
              )}
            />
          </DataTable>
        </>
      )}

      <RemoveFromListDialog
        customer={pendingRemoval}
        saving={removing}
        errorMessage={removeError}
        onConfirm={handleConfirmRemoval}
        onClose={() => {
          setPendingRemoval(null);
          setRemoveError(null);
        }}
      />
    </CollapsibleSection>
  );
}
